import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export type SubmissionType = 'permit' | 'client_submission' | 'internal' | 'engineering' | 'general';

export interface Submission {
  id: string;
  tenant_id: string;
  project_id: string;
  name: string;
  type: SubmissionType;
  due_date: string | null;
  sort_order: number;
  deliverable_count: number;
  completed_count: number;
  active_count: number;
  created_at: string;
  updated_at: string;
}

export interface SubmissionCreate {
  name: string;
  type?: SubmissionType;
  due_date?: string;
  sort_order?: number;
}

export interface SubmissionUpdate {
  name?: string;
  type?: SubmissionType;
  due_date?: string;
  sort_order?: number;
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useSubmissions(projectId: string | undefined) {
  return useQuery({
    queryKey: ['submissions', projectId],
    queryFn: async () => {
      const response = await api.submissions.list(projectId!);
      return response.submissions as Submission[];
    },
    enabled: !!projectId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; submission: SubmissionCreate }) =>
      api.submissions.create(data.projectId, data.submission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      toast.success('Submission created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create submission');
    },
  });
}

export function useUpdateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SubmissionUpdate }) =>
      api.submissions.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update submission');
    },
  });
}

export function useDeleteSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (submissionId: string) =>
      api.submissions.delete(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('Submission deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete submission');
    },
  });
}
